/**
 * The user's default refurbishment budget (`PATCH /api/me/preferences
 * { refurbishmentDefault, expectedRefurbishmentDefaultRevision }`). Money is
 * staged as exact pence text, including invalid edits, until Save; pounds are
 * converted by string arithmetic and never silently rounded.
 */
import type { RefurbishmentPreference, RefurbishmentScope } from '../../api/types'
import { formatSettingsMoney, shiftSettingsDecimal } from './settingsFieldPresentation'

export const DEFAULT_REFURBISHMENT_PREFERENCE: RefurbishmentPreference = { mode: 'auto', profile: 'standard' }

export interface RefurbishmentDraft {
  mode: RefurbishmentPreference['mode']
  profile: RefurbishmentScope
  amountPence: string | null
  ratePence: string | null
  areaUnit: 'sqm' | 'sqft'
}

export function refurbishmentDraft(preference: RefurbishmentPreference = DEFAULT_REFURBISHMENT_PREFERENCE): RefurbishmentDraft {
  return {
    mode: preference.mode,
    profile: preference.mode === 'auto' ? preference.profile : 'standard',
    amountPence: preference.mode === 'fixed' ? String(preference.amountPence) : '0',
    ratePence: preference.mode === 'rate' ? String(preference.ratePence) : null,
    areaUnit: preference.mode === 'rate' ? preference.areaUnit : 'sqm',
  }
}

/** Validate the contract shape before it is sent; the server applies the same rules. */
export function parseRefurbishmentPreference(value: unknown): RefurbishmentPreference {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Refurbishment preference must be an object.')
  const raw = value as Record<string, unknown>
  const keys = raw.mode === 'none' ? ['mode'] : raw.mode === 'auto' ? ['mode', 'profile'] : raw.mode === 'fixed' ? ['mode', 'amountPence'] : raw.mode === 'rate' ? ['mode', 'ratePence', 'areaUnit'] : []
  if (!keys.length || Object.keys(raw).some(key => !keys.includes(key)) || keys.some(key => !(key in raw))) throw new Error('Refurbishment preference contains missing or unsupported fields.')
  if (raw.mode === 'none') return { mode: 'none' }
  if (raw.mode === 'auto') {
    if (raw.profile !== 'light' && raw.profile !== 'standard' && raw.profile !== 'full') throw new Error('Choose a Light, Standard or Full refurbishment scope.')
    return { mode: 'auto', profile: raw.profile }
  }
  if (raw.mode === 'fixed') {
    if (!Number.isSafeInteger(raw.amountPence) || Number(raw.amountPence) < 0) throw new Error('Enter a non-negative refurbishment amount in whole pence.')
    return { mode: 'fixed', amountPence: raw.amountPence as number }
  }
  if (!Number.isSafeInteger(raw.ratePence) || Number(raw.ratePence) <= 0 || (raw.areaUnit !== 'sqm' && raw.areaUnit !== 'sqft')) throw new Error('Enter a positive refurbishment rate and choose its area unit.')
  return { mode: 'rate', ratePence: raw.ratePence as number, areaUnit: raw.areaUnit }
}

function exactPence(value: string | null, rate: boolean): number {
  const message = rate ? 'Enter a rate greater than £0, with no more than two decimal places.' : 'Enter a budget of £0 or more, with no more than two decimal places.'
  if (value === null || !/^(0|[1-9]\d*)$/.test(value)) throw new Error(message)
  if (value.length > 16) throw new Error('Enter a smaller refurbishment amount.')
  const integer = BigInt(value)
  if (integer > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error('Enter a smaller refurbishment amount.')
  if (rate && integer === 0n) throw new Error(message)
  return Number(integer)
}

export function resolveRefurbishmentDraft(draft: RefurbishmentDraft): RefurbishmentPreference {
  if (draft.mode === 'none') return parseRefurbishmentPreference({ mode: 'none' })
  if (draft.mode === 'auto') return parseRefurbishmentPreference({ mode: 'auto', profile: draft.profile })
  if (draft.mode === 'fixed') return parseRefurbishmentPreference({ mode: 'fixed', amountPence: exactPence(draft.amountPence, false) })
  if (draft.mode === 'rate') return parseRefurbishmentPreference({ mode: 'rate', ratePence: exactPence(draft.ratePence, true), areaUnit: draft.areaUnit })
  throw new Error('Choose a refurbishment budget method.')
}

export function refurbishmentPreferenceLabel(value: RefurbishmentPreference): string {
  if (value.mode === 'none') return 'None'
  if (value.mode === 'auto') return `Auto · ${value.profile === 'light' ? 'Light' : value.profile === 'full' ? 'Full' : 'Standard'}`
  const amount = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 }).format((value.mode === 'fixed' ? value.amountPence : value.ratePence) / 100)
  return value.mode === 'fixed' ? `Fixed · ${amount}` : `${amount}/${value.areaUnit === 'sqft' ? 'sq ft' : 'm²'}`
}

export function refurbishmentDraftSummary(draft: RefurbishmentDraft): string {
  try {
    const preference = resolveRefurbishmentDraft(draft)
    if (preference.mode === 'none' || preference.mode === 'auto') return refurbishmentPreferenceLabel(preference)
    const pence = preference.mode === 'fixed' ? preference.amountPence : preference.ratePence
    const amount = `£${formatSettingsMoney(shiftSettingsDecimal(String(pence), -2)!)}`
    return preference.mode === 'fixed' ? `Fixed · ${amount}` : `${amount}/${preference.areaUnit === 'sqft' ? 'sq ft' : 'm²'}`
  } catch {
    if (draft.mode === 'fixed') return 'Fixed · Enter a valid amount'
    if (draft.mode === 'rate') return `Per ${draft.areaUnit === 'sqft' ? 'sq ft' : 'm²'} · Enter a valid rate`
    if (draft.mode === 'auto') return 'Auto · Choose a works scope'
    return 'Choose a budget method'
  }
}
